import { cn } from '@/lib/utils';
import { getDraftBoardPosColor } from '@/lib/nflDraftBoardColors';
import { draftableSlots } from '@/lib/nflRosterSlots';
import type { RailPlayer } from './RosterRail';

/**
 * SAB-871: the MY TEAM panel for the mobile draft room.
 *
 * WHY THIS IS NOT RosterRail. RosterRail answers a different question — "show me ANY team's
 * roster", with a team selector and prev/next stepper across the league. On mobile MY TEAM
 * means exactly one team, the user's own, so the selector is not a smaller version of
 * itself, it is absent. Reusing RosterRail would have meant passing a `teams` array and a
 * selection callback that the tab has no concept of.
 *
 * What IS shared is the slot-row shape — the same slot badge, the same
 * "name / team · pick" two-line body, the same filled-vs-empty treatment — so the two read
 * as the same object at two sizes. Rows are taller here (44px targets) and the panel does
 * not own its own scroll: MobileDraftRoom's panel region is the only scroll region.
 */

export interface MobileMyTeamPanelProps {
  /** Explicit, already-expanded slot list. Includes IDP and IR. */
  slotOrder: string[];
  /** playerId -> slot name, authoritative, from the server. */
  slotAssignments: Record<string, string>;
  playersById: Map<string, RailPlayer>;
  rosterPlayerIds: string[];
  teamName: string | null;
}

export default function MobileMyTeamPanel({
  slotOrder, slotAssignments, playersById, rosterPlayerIds, teamName,
}: MobileMyTeamPanelProps) {
  const bySlot = new Map<string, string>();
  for (const pid of rosterPlayerIds) {
    const slot = slotAssignments[pid];
    if (slot && !bySlot.has(slot)) bySlot.set(slot, pid);
  }
  // SAB-986: the denominator is draftable capacity, not every slot -- IR is not reachable
  // by a pick, so counting it made every finished team read 17 / 18 forever. The numerator
  // is filtered over the same set so the two can never disagree.
  const draftable = draftableSlots(slotOrder);
  const filled = draftable.filter(s => bySlot.has(s)).length;

  return (
    <div data-testid="mobile-myteam-panel" className="flex flex-col gap-1.5 p-3">
      <div className="flex items-baseline justify-between px-0.5">
        <span className="truncate font-display text-[12px] font-bold text-white">
          {teamName ?? 'Your team'}
        </span>
        <span data-testid="mobile-myteam-count" className="flex-none font-display text-[11px] font-bold tabular-nums text-muted-foreground">
          {filled} / {draftable.length}
        </span>
      </div>

      {slotOrder.map(slot => {
        const pid = bySlot.get(slot);
        const p = pid ? playersById.get(pid) : undefined;
        const c = getDraftBoardPosColor(p?.position ?? slot.replace(/\d+$/, ''));
        return (
          <div
            key={slot}
            data-testid="mobile-roster-slot"
            data-slot={slot}
            data-filled={p ? 'true' : 'false'}
            className={cn(
              'flex min-h-11 items-center gap-2.5 rounded-lg px-3 py-2',
              p ? 'bg-muted' : 'border border-dashed border-border',
            )}
          >
            <span
              className={cn(
                'inline-flex h-[22px] w-[38px] flex-none items-center justify-center rounded border font-display text-[10px] font-bold',
                p ? cn(c.text, c.border, c.bgFaint) : 'border-border/20 bg-muted/10 text-muted-foreground',
              )}
            >
              {slot}
            </span>
            {p ? (
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13.5px] font-semibold text-white">{p.full_name}</span>
                <span className="block truncate text-[11.5px] text-muted-foreground">
                  {[p.nfl_team, p.pickLabel].filter(Boolean).join(' · ')}
                </span>
              </span>
            ) : (
              <span className="text-[13px] text-muted-foreground">Empty</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
